import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Button, MenuItem, useTheme } from "@mui/material";

import { usePlayer } from "@/components/player";
import { FeaturesHooks } from "@/hooks";
import { ButtonMenu } from "@/primitives";
import { AppRoutes } from "@/routes";

export const PlaybackStoryButton: React.FC<{
  suppressNavElements?: boolean;
}> = ({ suppressNavElements }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const { player, selectTrack } = usePlayer();
  const { canPickTrackToPlay } = FeaturesHooks.useFeatureFlags();

  const handleStoryNavigation = useCallback(() => {
    if (suppressNavElements) return;
    if (player.story?.id) {
      navigate(AppRoutes.STORY(player.story.id));
    }
  }, [player.story, navigate, suppressNavElements]);

  const storyTitle = useMemo(
    () => player.story?.content.title?.replace(/["]+/g, ""),
    [player.story?.content.title],
  );

  const showVoiceSelector = useMemo(() => {
    if (suppressNavElements) return;
    return (
      canPickTrackToPlay &&
      player.story &&
      player.story.content.tracks.length > 1
    );
  }, [suppressNavElements, canPickTrackToPlay, player.story]);

  const handleTrackChange = useCallback(
    (trackId: string) => {
      selectTrack(trackId);
    },
    [selectTrack],
  );

  const menu = useMemo(() => {
    return player.story?.content.tracks.map((track, num) => (
      <MenuItem
        key={track.id}
        onClick={() => handleTrackChange(track.id)}
        selected={player.track?.id === track.id}
      >
        {track?.params?.voiceName || `Track ${num}`}
      </MenuItem>
    ));
  }, [handleTrackChange, player.story?.content.tracks, player.track?.id]);

  const sx = {
    bgcolor: "primary.main",
    color: "primary.contrastText",
    display: "block",
    fontSize: theme.typography.pxToRem(11),
    fontWeight: "500",
    left: "50%",
    letterSpacing: "0.0",
    lineHeight: "1rem",
    maxWidth: "66%",
    overflow: "hidden",
    position: "absolute",
    px: 0.99,
    py: 0.11,
    textOverflow: "ellipsis",
    textTransform: "none",
    top: 0,
    transform: "translate(-50%, -50%)",
    whiteSpace: "nowrap",
  };

  if (showVoiceSelector) {
    return (
      <ButtonMenu
        onClick={handleStoryNavigation}
        sx={sx}
        menuProps={{
          anchorOrigin: { horizontal: "center", vertical: "top" },
          transformOrigin: { horizontal: "center", vertical: "bottom" },
        }}
        menu={menu}
      >
        {storyTitle}
      </ButtonMenu>
    );
  }

  return (
    <Button
      size="small"
      sx={sx}
      onClick={handleStoryNavigation}
      variant="contained"
    >
      {storyTitle}
    </Button>
  );
};
